import { z } from 'zod';
import { isValidRRule } from './recurrence.js';
import { isValidTimeZone } from './timezone.js';
function nonEmpty(shape) {
    return z
        .object(shape)
        .partial()
        .refine((value) => Object.keys(value).length > 0, { message: 'Nada para atualizar.' });
}
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const hhmm = z.string().regex(/^\d{2}:\d{2}$/);
/** Fuso IANA ("America/Sao_Paulo"). */
const ianaTimeZone = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isValidTimeZone, { message: 'Fuso horário desconhecido.' });
/** Regra de repetição no formato do padrão de calendário (ver `recurrence.ts`). */
const rruleText = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .refine(isValidRRule, { message: 'Regra de repetição inválida.' });
/** Antecedência máxima de um alerta: quatro semanas, em minutos. */
export const MAX_ALERT_MINUTES = 28 * 24 * 60;
export const MAX_ALERTS = 5;
/** Alertas: minutos antes do início (0 = na hora). */
const alertMinutes = z.array(z.number().int().min(0).max(MAX_ALERT_MINUTES)).max(MAX_ALERTS);
const idempotencyKey = z
    .string()
    .trim()
    .min(8)
    .max(80)
    .regex(/^[A-Za-z0-9._:-]+$/);
// ── Contatos ─────────────────────────────────────────────────────────────
/** Convite mandado a um contato: aguardando ou já aceito (a pessoa entrou no app). */
export const contactInviteSchema = z.object({
    contactId: z.uuid(),
    name: z.string(),
    invitedAt: z.iso.datetime(),
    lastSentAt: z.iso.datetime(),
    sentCount: z.number().int(),
    /** @usuário de quem entrou; null enquanto não entrou. */
    joinedUsername: z.string().nullable(),
});
export const contactInviteListSchema = z.object({ items: z.array(contactInviteSchema) });
export const contactSchema = z.object({
    id: z.uuid(),
    displayName: z.string(),
    preferredName: z.string().nullable(),
    phone: z.string().nullable(),
    category: z.string().nullable(),
    subcategory: z.string().nullable(),
    notes: z.string().nullable(),
    interactionCount: z.number().int().nonnegative(),
    lastInteractionAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    /** @usuário no Secretário quando o telefone do contato já tem conta. */
    appUsername: z.string().nullable(),
});
export const contactOverrideSchema = z.object({
    automationMode: z.enum(['automatico', 'semiauto', 'desativado']),
    requiresApproval: z.boolean(),
    neverAutomate: z.boolean(),
});
export const contactActivitySchema = z.object({
    id: z.uuid(),
    kind: z.enum(['reply', 'wait']),
    text: z.string(),
    createdAt: z.iso.datetime(),
});
export const contactDetailSchema = z.object({
    contact: contactSchema,
    override: contactOverrideSchema.nullable(),
    activity: z.array(contactActivitySchema),
});
export const createContactSchema = z.object({
    displayName: z.string().trim().min(1).max(80),
    phone: z.string().trim().min(8).max(32).optional(),
    category: z.string().trim().min(1).max(40).optional(),
});
/**
 * Importação da agenda do aparelho: só nome e telefone, com teto por pedido.
 * Tolerante de propósito: um contato fora do formato é pulado no servidor e
 * não derruba o lote inteiro.
 */
export const importContactsSchema = z.object({
    contacts: z
        .array(z.object({
        displayName: z.string().max(500),
        phone: z.string().max(120),
    }))
        .min(1)
        .max(1000),
});
/** Quais destes telefones já estão nos contatos (para a importação não repetir). */
export const knownPhonesSchema = z.object({
    phones: z.array(z.string().trim().min(1).max(32)).min(1).max(2000),
});
export const importContactsResultSchema = z.object({
    imported: z.number().int().nonnegative(),
    skipped: z.number().int().nonnegative(),
});
export const updateContactSchema = nonEmpty({
    displayName: z.string().trim().min(1).max(80),
    preferredName: z.string().trim().max(40).nullable(),
    category: z.string().trim().min(1).max(40).nullable(),
    subcategory: z.string().trim().max(40).nullable(),
    notes: z.string().trim().max(2000).nullable(),
});
export const contactListQuerySchema = z.object({
    category: z.string().trim().max(40).optional(),
    search: z.string().trim().max(80).optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(60),
    sort: z.enum(['lastInteractionAt', 'createdAt', 'displayName']).default('lastInteractionAt'),
});
export const contactListSchema = z.object({
    data: z.array(contactSchema),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
});
export const contactCategoryCountsSchema = z.object({
    categories: z.array(z.object({ category: z.string(), count: z.number().int() })),
    total: z.number().int(),
});
// ── Agenda ───────────────────────────────────────────────────────────────
export const appointmentStatusSchema = z.enum(['scheduled', 'pending', 'cancelled', 'completed']);
export const appointmentSchema = z.object({
    /**
     * Único por item da lista. Numa ocorrência de série é um identificador
     * próprio daquela data; para editar ou apagar, use `seriesId`.
     */
    id: z.uuid(),
    title: z.string(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    status: appointmentStatusSchema,
    contactId: z.uuid().nullable(),
    contactName: z.string().nullable(),
    notes: z.string().nullable(),
    // ── Agenda v2: tudo opcional, para o app antigo e o servidor antigo ──
    /** Fuso IANA em que a hora do compromisso vale. */
    timezone: z.string().optional(),
    /** Dia inteiro: começa à meia-noite local e `endsAt` é a meia-noite depois do último dia. */
    allDay: z.boolean().optional(),
    /** Regra de repetição (só no compromisso que define a série). */
    rrule: z.string().nullable().optional(),
    /** Alertas: minutos antes do início. */
    alerts: z.array(z.number().int().nonnegative()).optional(),
    location: z.string().nullable().optional(),
    /** Na lixeira desde quando. */
    deletedAt: z.iso.datetime().nullable().optional(),
    /** Numa ocorrência de série: o compromisso que guarda a regra. */
    seriesId: z.uuid().nullable().optional(),
    /** Numa ocorrência de série: o início original dela (a chave do "só este"). */
    occurrenceStart: z.iso.datetime().nullable().optional(),
    /** Ocorrência que já foi alterada sozinha ("só este"). */
    isException: z.boolean().optional(),
});
export const createAppointmentSchema = z.object({
    title: z.string().trim().min(1).max(120),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().optional(),
    contactId: z.uuid().optional(),
    contactName: z.string().trim().max(80).optional(),
    notes: z.string().trim().max(1000).optional(),
    /** Sem fuso, vale o da pessoa (Ajustes da agenda). */
    timezone: ianaTimeZone.optional(),
    allDay: z.boolean().optional(),
    rrule: rruleText.optional(),
    alerts: alertMinutes.optional(),
    location: z.string().trim().max(200).optional(),
    /** A mesma chave nunca cria dois: repetir o pedido devolve o que já foi criado. */
    idempotencyKey: idempotencyKey.optional(),
});
/** Numa série: "só este" ou "este e os próximos". O passado nunca muda. */
export const seriesScopeSchema = z.enum(['this', 'following']);
export const updateAppointmentSchema = nonEmpty({
    title: z.string().trim().min(1).max(120),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime().nullable(),
    status: appointmentStatusSchema,
    notes: z.string().trim().max(1000).nullable(),
    timezone: ianaTimeZone,
    allDay: z.boolean(),
    /** `null` tira a repetição. */
    rrule: rruleText.nullable(),
    alerts: alertMinutes,
    location: z.string().trim().max(200).nullable(),
    /** Obrigatórios quando o compromisso se repete. */
    scope: seriesScopeSchema,
    occurrenceStart: z.iso.datetime(),
});
/** Apagar (vai para a lixeira). Numa série, sem `scope` apaga a série inteira. */
export const deleteAppointmentSchema = z.object({
    scope: seriesScopeSchema.optional(),
    occurrenceStart: z.iso.datetime().optional(),
});
/** Restaurar da lixeira. Com `occurrenceStart`, desfaz um "só este" apagado. */
export const restoreAppointmentSchema = z.object({
    occurrenceStart: z.iso.datetime().optional(),
});
export const appointmentRangeSchema = z.object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** "true" inclui também o que começou antes de `from` e ainda não tinha acabado. */
    overlap: z.enum(['true', 'false']).optional(),
});
export const availabilityStatusSchema = z.enum(['available', 'busy']);
export const availabilityBlockSchema = z.object({
    id: z.uuid(),
    status: availabilityStatusSchema,
    weekday: z.number().int().min(0).max(6).nullable(),
    date: isoDate.nullable(),
    startTime: hhmm,
    endTime: hhmm,
    label: z.string().nullable(),
    active: z.boolean(),
});
export const availabilityBlockInputSchema = z
    .object({
    status: availabilityStatusSchema,
    weekday: z.number().int().min(0).max(6).nullable().optional(),
    date: isoDate.nullable().optional(),
    startTime: hhmm,
    endTime: hhmm,
    label: z.string().trim().max(60).nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((value) => (value.weekday != null) !== (value.date != null), {
    message: 'Escolha um dia da semana ou uma data.',
})
    .refine((value) => value.startTime < value.endTime, { message: 'O fim vem depois do início.' });
export const agendaSettingsSchema = z.object({
    enabled: z.boolean(),
    timezone: z.string(),
    defaultSlotMinutes: z.number().int().positive(),
    requireOwnerApproval: z.boolean(),
});
export const updateAgendaSettingsSchema = nonEmpty({
    enabled: z.boolean(),
    timezone: z
        .string()
        .trim()
        .min(1)
        .max(40)
        .refine(isValidTimeZone, { message: 'Fuso horário desconhecido.' }),
    defaultSlotMinutes: z.number().int().min(5).max(480),
    requireOwnerApproval: z.boolean(),
});
export const reminderSchema = z.object({
    id: z.uuid(),
    message: z.string(),
    remindAt: z.iso.datetime(),
    /**
     * Hora do lembrete por extenso, no fuso do lembrete. O nome é antigo (o fuso
     * era sempre Brasília); o app novo lê `remindAtLocal`.
     */
    remindAtBrasilia: z.string(),
    status: z.enum(['pending', 'sent', 'cancelled']),
    createdAt: z.iso.datetime(),
    // ── Agenda v2: tudo opcional ──
    /** Fuso IANA em que a data e a hora foram ditas. */
    timezone: z.string().optional(),
    remindAtLocal: z.string().optional(),
    /** Na lixeira desde quando. */
    deletedAt: z.iso.datetime().nullable().optional(),
});
export const createReminderSchema = z.object({
    message: z.string().trim().min(1).max(200),
    date: isoDate,
    time: hhmm,
    /** Sem fuso, vale o da pessoa (Ajustes da agenda). */
    timezone: ianaTimeZone.optional(),
    /** A mesma chave nunca cria dois. */
    idempotencyKey: idempotencyKey.optional(),
});
/** Quantos dias um item apagado fica na lixeira antes de sumir de vez. */
export const AGENDA_TRASH_DAYS = 30;
export const agendaTrashSchema = z.object({
    retentionDays: z.number().int().positive(),
    /** Compromissos e séries apagados, e ocorrências apagadas sozinhas (com `seriesId`). */
    appointments: z.array(appointmentSchema),
    reminders: z.array(reminderSchema),
});
// ── Sobre você ───────────────────────────────────────────────────────────
export const ownerFactCategorySchema = z.enum([
    'voce',
    'familia',
    'casa',
    'carro',
    'trabalho',
    'gostos',
]);
export const ownerFactSchema = z.object({
    id: z.uuid(),
    category: ownerFactCategorySchema,
    factKey: z.string(),
    label: z.string(),
    value: z.string(),
    source: z.enum(['owner', 'conversation']),
    /**
     * Quando a pessoa confirmou uma lembrança que o Secretário anotou conversando.
     * Opcional: respostas de antes deste campo continuam válidas.
     */
    confirmedAt: z.iso.datetime().nullable().optional(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
});
/** Lembrança que o Secretário anotou sozinho e a pessoa ainda não confirmou. */
export function ownerFactNeedsConfirmation(fact) {
    return fact.source === 'conversation' && !fact.confirmedAt;
}
export const createOwnerFactSchema = z.object({
    category: ownerFactCategorySchema,
    label: z.string().trim().min(1).max(80),
    value: z.string().trim().min(1).max(300),
});
export const updateOwnerFactSchema = nonEmpty({
    label: z.string().trim().min(1).max(80),
    value: z.string().trim().min(1).max(300),
});
// ── Aprovações ───────────────────────────────────────────────────────────
export const approvalStatusSchema = z.enum([
    'aguardando',
    'aprovado',
    'recusado',
    'expirado',
    'descartado',
]);
export const approvalRequestSchema = z.object({
    id: z.uuid(),
    contactId: z.uuid().nullable(),
    contactName: z.string().nullable(),
    headline: z.string().nullable(),
    question: z.string().nullable(),
    draft: z.string().nullable(),
    originalMessage: z.string().nullable(),
    suggestedDate: z.string().nullable(),
    suggestedTime: z.string().nullable(),
    /** Conversa do mensageiro onde o rascunho aprovado é enviado (ou nada). */
    roomId: z.string().nullable().default(null),
    status: approvalStatusSchema,
    createdAt: z.iso.datetime(),
});
export const approvalActionSchema = z.enum(['approve', 'reject', 'discard']);
export const approvalActSchema = z.object({
    action: approvalActionSchema,
    instruction: z.string().trim().min(1).max(500).optional(),
});
export const approvalBulkSchema = z.object({
    ids: z.array(z.uuid()).min(1).max(100),
    action: approvalActionSchema,
});
// ── Persona (Jeito único) ────────────────────────────────────────────────
export const personaRouteSchema = z.object({
    trigger: z.string().trim().max(120),
    action: z.string().trim().max(200),
});
export const personaAnswersSchema = z.object({
    about: z.string().trim().max(1500).optional(),
    speechStyle: z.string().trim().max(1000).optional(),
    familyTone: z.string().trim().max(300).optional(),
    workTone: z.string().trim().max(300).optional(),
    clientTone: z.string().trim().max(300).optional(),
    canSay: z.string().trim().max(600).optional(),
    cannotSay: z.string().trim().max(600).optional(),
    routes: z.array(personaRouteSchema).max(10).optional(),
    neverPhrases: z.string().trim().max(600).optional(),
    signoff: z.string().trim().max(200).optional(),
});
/**
 * Chave de comparação de telefone feita igual no aparelho e no servidor: DDD
 * e os 8 últimos dígitos, sem o 55 e sem o nono dígito. Serve para o aparelho
 * saber quem já está nos contatos sem mandar a agenda inteira para o servidor.
 */
export function phoneMatchKey(raw) {
    let digits = raw.replace(/\D/g, '').replace(/^0+/, '');
    if (digits.length >= 12 && digits.startsWith('55'))
        digits = digits.slice(2);
    if (digits.length === 11 && digits[2] === '9')
        digits = digits.slice(0, 2) + digits.slice(3);
    return digits.length === 10 ? digits : null;
}
